import '../../theme/css/global.css';
import {
  Box, Container, Dialog, Divider, IconButton, Slide, Tooltip, Typography, useMediaQuery, useTheme, Grid, Paper,
} from '@mui/material';

import {
  forwardRef, useEffect, useState,
} from 'react';

import { TransitionProps } from '@mui/material/transitions';
import { Delete, PictureAsPdf } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/Hooks';
import ClientType from '../../models/ClientType';
import Profile from './components/Profile';
import Header from './components/Header';
import themeConfig from '../../theme/ThemeConfig';
import Suivi from '../../models/Suivi';
import SuivisList from './components/SuivisList';
import Spinner from '../Spinner';
import AlertUtils from '../../utils/AlertUtil';
import ClientActions from './components/ClientActions';
import DraftSuiviAdd from './components/DraftSuiviAdd';
import DeleteClientConfirmation from '../../common/DeleteClientConfirmation';
import { setClientPopupOpen, setClientOpenId } from '../../store/slices/ClientSlice';
import Utils from '../../utils/Utils';
import translator from '../../theme/translator.json';
import SupportedLanguages from '../../models/enums/SupportedLanguages';
import FilesSave from './components/FilesSave';
import VisitesList from './components/VisitesList';
import PricingPlan from '../../models/enums/PricingPlans';
import PdfExportService from '../../services/PdfExportService';

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) => <Slide direction="up" ref={ref} {...props} />);

const ClientsSummary: React.FC = () => {
  const dispatch = useAppDispatch();
  const { suivis, clients, user } = useAppSelector((state) => state.user);
  const { clientPopupOpen, orderedClientsList, clientOpenId } = useAppSelector((state) => state.client);
  const language = useAppSelector((state) => state.user.language) as SupportedLanguages;

  const [loading, setLoading] = useState(false);

  const [deleteClientDialogOpen, setDeleteClientDialogOpen] = useState(false);
  const [currentClient, setCurrentClient] = useState(undefined) as any | ClientType;
  const [currentSuivis, setCurrentSuivis] = useState([]) as any | Suivi[];
  const [currentClientIndex, setCurrentClientIndex] = useState(undefined) as any;
  const [isOpenTimeoutDone, setIsOpenTimeoutDone] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    try {
      setLoading(true);
      if (clientOpenId && clients && clientPopupOpen) {
        const clientEntryPoint = orderedClientsList.find((c:ClientType, index: number) => {
          if (c.id === clientOpenId) {
            setCurrentClientIndex(index);
            return c;
          }
          return undefined;
        });
        setCurrentClient(clientEntryPoint);
        if (clientEntryPoint) {
          const tempCurrentSuivis = suivis.filter((suivi: Suivi) => suivi.clientId === clientEntryPoint.id);
          setCurrentSuivis(tempCurrentSuivis);
        } else {
          handleCloseDialog();
        }
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
      AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
    }
  }, [clientPopupOpen, clientOpenId]);

  useEffect(() => {
    try {
      if (currentClient && clientPopupOpen && suivis) {
        // sur tout les suivis on recupere les suivis du client.
        const tempCurrentSuivis: Suivi[] = suivis.filter((suivi: Suivi) => suivi.clientId === currentClient.id);
        // Trier par date décroissante (plus récent -> plus ancien)
        tempCurrentSuivis.sort((a, b) => b.date.seconds - a.date.seconds);
        setCurrentSuivis(tempCurrentSuivis);
      }
    } catch (e) {
      setLoading(false);
      console.error(e);
      AlertUtils.createErrorAlert(Utils.getTranslation(language, translator.errorMessages.general.unknown), dispatch);
    }
  }, [suivis, currentClient]);

  // Mettre à jour currentClient quand les données du client changent
  useEffect(() => {
    try {
      if (currentClient && clientOpenId && orderedClientsList && clientPopupOpen) {
        const updatedClient = orderedClientsList.find((c: ClientType) => c.id === clientOpenId);
        if (updatedClient && JSON.stringify(updatedClient) !== JSON.stringify(currentClient)) {
          setCurrentClient(updatedClient);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [orderedClientsList, clientOpenId, clientPopupOpen]);

  useEffect(() => {
    if (isOpenTimeoutDone) return;
    triggerOpenTimeout();
  }, [isOpenTimeoutDone]);

  const triggerOpenTimeout = () => {
    setTimeout(() => setIsOpenTimeoutDone(true), 1000);
  };

  const onClose = () => {
    dispatch(setClientPopupOpen(false));
  };

  const handleCloseDialog = () => {
    setCurrentClientIndex(undefined);
    setCurrentClient(undefined);
    setCurrentSuivis([]);
    setIsOpenTimeoutDone(false);
    onClose();
  };

  const handleCloseDialogBackdrop = (event:any, reason:any) => {
    if (reason && reason === 'backdropClick' && !isOpenTimeoutDone) { return; }
    setCurrentClientIndex(undefined);
    setCurrentClient(undefined);
    setCurrentSuivis([]);
    setIsOpenTimeoutDone(false);
    onClose();
  };

  const handleNextClient = () => {
    const foundCurrentIndex = orderedClientsList.findIndex((c: ClientType) => c.id === currentClient.id);

    if (foundCurrentIndex + 1 >= orderedClientsList.length || currentClientIndex < 0 || currentClientIndex + 1 >= orderedClientsList.length) {
      // Aller au premier client
      setCurrentClient(orderedClientsList[0]);
      setCurrentClientIndex(0);
      dispatch(setClientOpenId(orderedClientsList[0].id));
    } else if (foundCurrentIndex === -1) {
      // Nous n'avons pas trouvé le client actuel dans liste, il a donc été transféré
      setCurrentClient(orderedClientsList[currentClientIndex]);
      setCurrentClientIndex(currentClientIndex);
      dispatch(setClientOpenId(orderedClientsList[currentClientIndex].id));
    } else {
      // Aller au client suivant
      setCurrentClient(orderedClientsList[foundCurrentIndex + 1]);
      setCurrentClientIndex(foundCurrentIndex + 1);
      dispatch(setClientOpenId(orderedClientsList[foundCurrentIndex + 1].id));
    }
  };

  const handlePreviousClient = () => {
    const foundCurrentIndex = orderedClientsList.findIndex((c: ClientType) => c.id === currentClient.id);

    if (foundCurrentIndex === 0 || currentClientIndex - 1 < 0 || currentClientIndex - 1 >= orderedClientsList.length) {
      // Aller au premier client
      setCurrentClient(orderedClientsList[0]);
      setCurrentClientIndex(0);
      dispatch(setClientOpenId(orderedClientsList[0].id));
    } else if (foundCurrentIndex === -1) {
      // Nous n'avons pas trouvé le client actuel dans liste, il a donc été transféré
      setCurrentClient(orderedClientsList[currentClientIndex - 1]);
      setCurrentClientIndex(currentClientIndex - 1);
      dispatch(setClientOpenId(orderedClientsList[currentClientIndex - 1].id));
    } else {
      // Aller au client précédent
      setCurrentClient(orderedClientsList[foundCurrentIndex - 1]);
      setCurrentClientIndex(foundCurrentIndex - 1);
      dispatch(setClientOpenId(orderedClientsList[foundCurrentIndex - 1].id));
    }
  };

  const handleExportPdf = async () => {
    if (!clientOpenId) return;

    try {
      setExportingPdf(true);

      // Récupérer les suivis les plus récents
      const latestSuivis = suivis.filter((suivi: Suivi) => suivi.clientId === clientOpenId);

      const pdfExportSettings = {
        includeContactInformation: user.pdfExportContactInformation ?? true,
        includeAdditionalInformation: user.pdfExportAdditionalInformation ?? true,
        includeClientDescription: user.pdfExportClientDescription ?? true,
        includeFollowUpHistory: user.pdfExportFollowUpHistory ?? true,
      };

      // 🚀 PdfExportService va fetch les données fraîches automatiquement
      await PdfExportService.exportClientSummaryToPdf(currentClient, latestSuivis, language, pdfExportSettings);
      AlertUtils.createSuccessAlert(
        language === 'fr' ? 'PDF exporté avec succès!' : 'PDF exported successfully!',
        dispatch,
      );
    } catch (error: any) {
      AlertUtils.createErrorAlert(
        error.message || (language === 'fr' ? 'Erreur lors de l\'export PDF' : 'Error during PDF export'),
        dispatch,
      );
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <>
      <Dialog
        maxWidth={false}
        fullWidth
        fullScreen={fullScreen}
        open={clientPopupOpen}
        onClose={handleCloseDialogBackdrop}
        disableEnforceFocus
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            // Sur mobile : plein écran normal
            // Sur desktop : effet "Bottom Sheet" large avec espace en haut
            width: '100%',
            maxWidth: '100%!important', // Force la largeur maximale à 100%
            height: fullScreen ? '100%' : 'calc(100%)', // Laisse 64px en haut pour cliquer
            maxHeight: '100%!important',
            position: fullScreen ? 'unset' : 'absolute',
            bottom: 0,
            m: 0, // Enlève toutes les marges
            borderRadius: fullScreen ? 0 : '16px 16px 0 0',
            overflowY: 'hidden', // Désactiver le scroll global du Paper
            display: 'flex', // Activer Flexbox
            flexDirection: 'column', // Empiler verticalement (Header + Contenu)
          },
        }}
        sx={{
          zIndex: 1300, // S'assurer qu'il est au-dessus
          '& .MuiDialog-container': {
            alignItems: 'flex-end', // S'assure que le dialogue est aligné en bas
          },
          '& .MuiPaper-root': {
            maxWidth: '100%!important', // Override supplémentaire pour sécurité
            m: '0!important', // Force la suppression des marges par défaut de MUI
          },
        }}
      >
        {currentClient && clientPopupOpen
          && <>
            <Box sx={{ flexShrink: 0 }}>
              <Header
                orderedClientsList={orderedClientsList}
                onPreviousClient={handlePreviousClient}
                clientIndex={currentClientIndex}
                onNextClient={handleNextClient}
                client={currentClient}
                onClose={handleCloseDialog}/>
            </Box>
            <Container
              id="client-summary-content"
              maxWidth={false} // Utiliser toute la largeur disponible dans le Paper
              sx={{
                px: { xs: 2, md: 6 }, // Padding horizontal
                pb: 4,
                flex: 1, // Prendre l'espace restant
                overflowY: 'auto', // Scroll uniquement ici
              }}>
              <Grid container spacing={4} sx={{ mt: 0 }}>
                {/* Colonne Gauche : Profil */}
                <Grid item xs={12} md={7} lg={8}>
                  <Profile handleCloseSummary={handleCloseDialog} client={currentClient} />
                </Grid>

                {/* Colonne Droite : Sidebar (Dates, Files) */}
                <Grid item xs={12} md={5} lg={4}>
                  <Paper variant="outlined" sx={{
                    p: 3, borderRadius: 3, backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0',
                  }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 1 }}>
                      <strong>{Utils.getTranslation(language, translator.components.ficheClient.dateProchainSuivi)}</strong>
                    </Typography>

                    <DraftSuiviAdd clientId={currentClient.id} nextSuivi={currentClient.nextSuivi} client={currentClient} />

                    <Box sx={{ mt: 4, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem' }}><strong>{Utils.getTranslation(language, translator.components.ficheClient.fichiers)}</strong></Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <FilesSave client={currentClient} />
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4,
              }}>
                <Typography variant="h6"><strong>{Utils.getTranslation(language, translator.components.ficheClient.modifyStatus)}</strong></Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={language === 'fr' ? 'Exporter en PDF' : 'Export to PDF'}>
                    <IconButton
                      size="small"
                      onClick={handleExportPdf}
                      disabled={exportingPdf}
                      color="primary"
                    >
                      <PictureAsPdf fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={Utils.getTranslation(language, translator.components.ficheClient.deleteClient)}>
                    <IconButton size="small" onClick={() => setDeleteClientDialogOpen(true) }>
                      <Delete fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Divider
                sx={{
                  m: themeConfig.spacing(2, 0, 3, 0),
                }} />
              <ClientActions client={currentClient} />
              {
                user?.plan === PricingPlan.PREMIUM && user.showVisites
                && <><Box sx={{
                  m: themeConfig.spacing(6, 0, 0, 0),
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '20px',
                    mr: 2,
                  }}>
                    <Typography variant="h6"><strong>{Utils.getTranslation(language, translator.components.ficheClient.visites.title)}</strong></Typography>
                  </Box>
                </Box>
                <Divider
                  sx={{
                    m: themeConfig.spacing(2, 0, 3, 0),
                  }} />
                <VisitesList client={currentClient} />
                </>}
              <Box sx={{
                m: themeConfig.spacing(6, 0, 0, 0),
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '20px',
                  mr: 2,
                }}>
                  <Typography variant="h6"><strong>{Utils.getTranslation(language, translator.components.ficheClient.notes)}</strong></Typography>
                </Box>
              </Box>
              <Divider
                sx={{
                  m: themeConfig.spacing(2, 0, 3, 0),
                }} />
              <Box>
                <SuivisList client={currentClient} suivis={currentSuivis} suiviFocusId={currentClient.suiviFocusId} />
              </Box>
            </Container>
          </>
        }
      </Dialog>
      <Spinner show={loading}/>
      {currentClient && <DeleteClientConfirmation
        open={deleteClientDialogOpen}
        handleClose={() => setDeleteClientDialogOpen(false)}
        handleCloseSummary={onClose}
        clients={[currentClient]}
      />}
    </>
  );
};

export default ClientsSummary;
